import { Directory, Filesystem } from '@capacitor/filesystem';
import { v4 as uuidv4 } from 'uuid';

// Photos / documents for the SQLite offline store (OFFLINE_PLAN.md Phase 4, 2026-09-29).
//
// Files are NOT put inside the encrypted database: passing megabytes through the native bridge as text on every
// read would make the app slow. They are written as plain files in the app's private, never-backed-up folder
// (iOS: Library/NoCloud, protected by iOS file protection while the phone is locked; Android: the app's own
// files folder, covered by the phone's storage encryption), and the database keeps only a small reference.

const DIR = Directory.LibraryNoCloud;
const ROOT = 'll-offline-files';

/** What the database stores in place of a Blob/File. */
export interface FileRef {
  __llFile: string; // path under ROOT
  type: string;
  size: number;
  name?: string;        // set for a File (keeps its file name)
  lastModified?: number;
}

export function isFileRef(v: unknown): v is FileRef {
  return !!v && typeof v === 'object' && typeof (v as any).__llFile === 'string';
}

// A Blob returned by list-style reads (filter) WITHOUT loading the file: size and type are right, the content is
// not loaded (only the file clean-up reads these, for their size). Writing one back keeps the file it points to.
const lazyRefs = new WeakMap<Blob, FileRef>();

export function lazyBlob(ref: FileRef): Blob {
  const blob = new Blob([], { type: ref.type });
  Object.defineProperty(blob, 'size', { get: () => ref.size });
  lazyRefs.set(blob, ref);
  return blob;
}

export function lazyRefOf(blob: Blob): FileRef | undefined {
  return lazyRefs.get(blob);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result ?? '');
      resolve(s.includes(',') ? s.substring(s.indexOf(',') + 1) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export async function writeBlob(table: string, blob: Blob): Promise<FileRef> {
  const path = `${ROOT}/${table}/${uuidv4()}`;
  await Filesystem.writeFile({ path, data: await blobToBase64(blob), directory: DIR, recursive: true });
  const file = blob as File;
  return {
    __llFile: path,
    type: blob.type || 'application/octet-stream',
    size: blob.size,
    ...(typeof file.name === 'string' ? { name: file.name, lastModified: file.lastModified } : {}),
  };
}

export async function readBlob(ref: FileRef): Promise<Blob> {
  const res = await Filesystem.readFile({ path: ref.__llFile, directory: DIR });
  const data = res.data;
  const blob = data instanceof Blob ? data : new Blob([base64ToBytes(String(data)) as BlobPart], { type: ref.type });
  return ref.name ? new File([blob], ref.name, { type: ref.type, lastModified: ref.lastModified }) : blob;
}

export async function deleteFile(ref: FileRef): Promise<void> {
  try {
    await Filesystem.deleteFile({ path: ref.__llFile, directory: DIR });
  } catch { /* already gone */ }
}

export async function deleteTableFiles(table: string): Promise<void> {
  try {
    await Filesystem.rmdir({ path: `${ROOT}/${table}`, directory: DIR, recursive: true });
  } catch { /* nothing there */ }
}

/** Every FileRef inside a stored value (any depth). */
export function collectFileRefs(value: unknown, out: FileRef[] = []): FileRef[] {
  if (isFileRef(value)) {
    out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((v) => collectFileRefs(v, out));
  } else if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((v) => collectFileRefs(v, out));
  }
  return out;
}
